/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable no-void */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  type DataThemeOptions,
  Loader,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import DOMPurify from 'dompurify';

import { QUILL_THEMES, testIds, TOKENS } from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import { type QuillDelta } from '../../types';
import ExternalLinkWarningModal from '../ExternalLinkWarningModal/ExternalLinkWarningModal';

import { isBlockedEmbed } from './helpers/isBlockedEmbed';

import 'quill/dist/quill.snow.css';
import './SaviyntQuill.css';

interface SaviyntRenderQuillDeltaProps {
  delta: QuillDelta | undefined;
  dataTheme?: DataThemeOptions;
  classNames?: {
    root?: string | null;
  };
  dataTestId?: string;
  /** When true, http(s) links show a confirmation modal before opening. */
  confirmExternalLinks?: boolean;
}

/**
 * If the anchor navigates to an http(s) URL (not same-tab hash, mailto, tel, or root-relative),
 * returns the resolved URL for `window.open`; otherwise null.
 */
export function resolveHttpHttpsNavUrl(
  anchor: HTMLAnchorElement
): string | null {
  const raw = anchor.getAttribute('href')?.trim();

  if (!raw) {
    return null;
  }

  const lower = raw.toLowerCase();

  if (
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:') ||
    raw.startsWith('#')
  ) {
    return null;
  }

  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return null;
  }

  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return anchor.href;
  }

  if (raw.startsWith('//')) {
    return anchor.href;
  }

  return null;
}

/**
 * Normalizes anchor links in HTML to ensure they are absolute URLs.
 * Adds https:// prefix to links without a scheme and sets target and rel attributes.
 */
export const normalizeAnchorLinks = (html: string): string => {
  if (!html) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const anchors = doc.querySelectorAll('a[href]');

  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href')?.trim();

    if (!href) {
      return;
    }

    const hrefLower = href.toLowerCase();
    const isAbsolute =
      hrefLower.startsWith('http://') ||
      hrefLower.startsWith('https://') ||
      hrefLower.startsWith('mailto:') ||
      hrefLower.startsWith('tel:') ||
      href.startsWith('#') ||
      href.startsWith('/');

    if (!isAbsolute) {
      anchor.setAttribute('href', `https://${href}`);
    }

    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });

  return doc.body.innerHTML;
};

function SaviyntRenderQuillDelta({
  delta,
  dataTheme,
  classNames = { root: null },
  dataTestId,
  confirmExternalLinks = false,
}: SaviyntRenderQuillDeltaProps) {
  const [htmlContent, setHtmlContent] = useState('');
  const [isQuillInitializing, setIsQuillInitializing] = useState(true);
  const [pendingExternalUrl, setPendingExternalUrl] = useState<string | null>(
    null
  );
  const hiddenEditorRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<any>(null);
  const deltaCtorRef = useRef<any>(null);
  const isInitializingRef = useRef(false);
  const renderedEditorRef = useRef<HTMLDivElement>(null);

  const l = useInvertedLocalize();

  const wrapperClasses = cx(
    'SaviyntRenderQuillDelta',
    isQuillInitializing && 'SaviyntRenderQuillDelta--isQuillInitializing',
    classNames.root
  );

  const normalizeDeltaForRender = (input: any) => {
    const ops = Array.isArray(input?.ops) ? input.ops : [];

    const cleanedOps = ops
      .filter((op: any) => !isBlockedEmbed(op))
      .map((op: any) => {
        const nextOp: any = { ...op };
        const attrs = nextOp.attributes;

        if (!attrs || typeof attrs !== 'object') {
          delete nextOp.attributes;

          return nextOp;
        }

        const nextAttrs: Record<string, unknown> = {};

        Object.keys(attrs).forEach((key) => {
          if (key !== 'background' && attrs[key] != null) {
            nextAttrs[key] = attrs[key];
          }
        });

        if (Object.keys(nextAttrs).length === 0) {
          delete nextOp.attributes;
        } else {
          nextOp.attributes = nextAttrs;
        }

        return nextOp;
      });

    return { ops: cleanedOps };
  };

  useEffect(() => {
    const initQuill = async () => {
      if (
        !hiddenEditorRef.current ||
        quillInstanceRef.current ||
        isInitializingRef.current
      ) {
        return;
      }

      isInitializingRef.current = true;
      setIsQuillInitializing(true);

      try {
        const { default: Quill } = await import('quill');
        const DeltaModule = Quill.import('delta') as any;
        const DeltaCtor = DeltaModule.default || DeltaModule;

        deltaCtorRef.current = DeltaCtor;

        quillInstanceRef.current = new Quill(hiddenEditorRef.current, {
          modules: { toolbar: false },
          theme: QUILL_THEMES.SNOW,
        });
      } catch (error) {
        if (error instanceof Error) {
          Logger.error(`Failed to initialize Quill: ${error.message}`);
        } else {
          Logger.error('Failed to initialize Quill');
        }
      } finally {
        isInitializingRef.current = false;
        setIsQuillInitializing(false);
      }
    };

    void initQuill();
  }, []);

  useEffect(() => {
    if (!quillInstanceRef.current || !deltaCtorRef.current) {
      return;
    }

    if (!delta?.ops) {
      setHtmlContent('');

      return;
    }

    const normalized = normalizeDeltaForRender(delta);
    const DeltaCtor = deltaCtorRef.current;

    quillInstanceRef.current.setContents(new DeltaCtor(normalized.ops));
    const rawHtml = quillInstanceRef.current.root.innerHTML as string;
    const sanitizedHtml = DOMPurify.sanitize(rawHtml);
    const normalizedHtml = normalizeAnchorLinks(sanitizedHtml);

    setHtmlContent(normalizedHtml);
  }, [delta, isQuillInitializing]);

  useEffect(() => {
    if (!confirmExternalLinks) {
      return undefined;
    }

    const el = renderedEditorRef.current;

    if (!el) {
      return undefined;
    }

    const onCaptureClick = (event: MouseEvent) => {
      const { target } = event;

      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a[href]');

      if (!(anchor instanceof HTMLAnchorElement)) {
        return;
      }

      const navUrl = resolveHttpHttpsNavUrl(anchor);

      if (!navUrl) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setPendingExternalUrl(navUrl);
    };

    el.addEventListener('click', onCaptureClick, true);

    return () => {
      el.removeEventListener('click', onCaptureClick, true);
    };
  }, [confirmExternalLinks, htmlContent]);

  if (!delta) {
    return null;
  }

  const renderContent = () => {
    if (isQuillInitializing && !quillInstanceRef.current) {
      return (
        <Box className='SaviyntRenderQuillDelta-loading'>
          <Loader kind='loop' format='inline' color='brand' size='large' />
          <Typography kind='body2' color={TOKENS.CF_NEU_STL}>
            {l('Loading content, please wait...')}
          </Typography>
        </Box>
      );
    }

    return (
      <div
        ref={renderedEditorRef}
        className='ql-editor'
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  return (
    <>
      <div ref={hiddenEditorRef} style={{ display: 'none' }} />
      <Box
        dataTheme={dataTheme}
        className={wrapperClasses}
        dataTestId={dataTestId}>
        {renderContent()}
      </Box>
      {confirmExternalLinks && (
        <ExternalLinkWarningModal
          isOpen={pendingExternalUrl !== null}
          onClose={() => {
            setPendingExternalUrl(null);
          }}
          onContinue={() => {
            if (pendingExternalUrl) {
              window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
            }

            setPendingExternalUrl(null);
          }}
          testIds={{
            modal: testIds.FF_QUILL_EXTERNAL_LINK_MODAL,
            continueButton: testIds.FF_QUILL_EXTERNAL_LINK_CONTINUE_BUTTON,
            cancelButton: testIds.FF_QUILL_EXTERNAL_LINK_CANCEL_BUTTON,
          }}
        />
      )}
    </>
  );
}

export default SaviyntRenderQuillDelta;
